"use client";

import {
  Box,
  Flex,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { useMemo, useState } from "react";
import { Text } from "@/components/ui/Text";
import { useClassScheduleGet } from "@/features/makeups/hooks";
import type { ClassScheduleEntry } from "@/openapi/generated";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useStudioClock } from "@/hooks/shared/useStudioClock";
import { studioTodayDate } from "@/lib/utils/date/studio-clock";
import { filterBookableScheduleSlots } from "./scheduleSlotValidity";
import { filterUpcomingScheduleSlots } from "./scheduleSlotTiming";
import {
  formatScheduleSlotDateLabelFromEntry,
  formatScheduleSlotTimeRangeFromEntry,
  initialScheduleMonthFromReference,
  makeupWeeksLaterLabel,
  scheduleSlotAriaLabel,
} from "./scheduleSlotDisplay";

function formatMonthLabel(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function isCurrentOrEarlierMonth(
  month: number,
  year: number,
  studioToday: Date,
): boolean {
  const viewed = year * 12 + (month - 1);
  const current = studioToday.getFullYear() * 12 + studioToday.getMonth();
  return viewed <= current;
}

export function scheduleSlotKey(entry: ClassScheduleEntry): string {
  const start =
    entry.start instanceof Date ? entry.start : new Date(entry.start);
  const end = entry.end instanceof Date ? entry.end : new Date(entry.end);
  return `${start.toISOString()}-${end.toISOString()}`;
}

type MakeupScheduleSlotPickerProps = {
  classId: number;
  className: string;
  selectedSlot: ClassScheduleEntry | null;
  onSelectSlot: (slot: ClassScheduleEntry | null) => void;
  enabled?: boolean;
  /** Hide slots that match this predicate (e.g. parent's current request). */
  excludeSlot?: (entry: ClassScheduleEntry) => boolean;
  /** When true, omits the intro paragraph above the month navigator. */
  hideDescription?: boolean;
  description?: string;
  /** Parent's requested date — used to label "Next week" / "In 2 weeks". */
  referenceDate?: Date | string | null;
  /** Later-week admin copy and relative week labels. */
  laterWeek?: boolean;
};

export function MakeupScheduleSlotPicker({
  classId,
  className,
  selectedSlot,
  onSelectSlot,
  enabled = true,
  excludeSlot,
  hideDescription = false,
  description,
  referenceDate = null,
  laterWeek = false,
}: MakeupScheduleSlotPickerProps) {
  const theme = useThemeTokens();
  const studioClock = useStudioClock();
  const todayDate = studioTodayDate(studioClock);
  const initialMonth = initialScheduleMonthFromReference(todayDate, referenceDate);
  const [scheduleMonth, setScheduleMonth] = useState(initialMonth.month);
  const [scheduleYear, setScheduleYear] = useState(initialMonth.year);

  const canGoToPreviousMonth = !isCurrentOrEarlierMonth(
    scheduleMonth,
    scheduleYear,
    todayDate,
  );

  const { data: scheduleData, isPending: isLoadingSchedule } =
    useClassScheduleGet({
      classId,
      month: scheduleMonth,
      year: scheduleYear,
      enabled: enabled && classId > 0,
    });

  const futureSlots = useMemo(() => {
    const bookable = filterBookableScheduleSlots(
      scheduleData?.schedule ?? [],
      studioClock.timeZone,
    );
    const upcoming = filterUpcomingScheduleSlots(
      bookable,
      undefined,
      studioClock.timeZone,
    );
    if (!excludeSlot) return upcoming;
    return upcoming.filter((entry) => !excludeSlot(entry));
  }, [excludeSlot, scheduleData?.schedule, studioClock.timeZone]);

  const shiftScheduleMonth = (delta: number) => {
    if (delta < 0 && !canGoToPreviousMonth) return;
    const d = new Date(scheduleYear, scheduleMonth - 1 + delta, 1);
    setScheduleMonth(d.getMonth() + 1);
    setScheduleYear(d.getFullYear());
    onSelectSlot(null);
  };

  const intro = description ?? (
    laterWeek ? (
      <>
        Later weeks of{" "}
        <Text as="span" fontWeight="600" color={theme.text.primary}>
          {className}
        </Text>
        {" "}
        — same class the parent asked for, just a week that still works.
      </>
    ) : (
      <>
        Pick an upcoming session for{" "}
        <Text as="span" fontWeight="600" color={theme.text.primary}>
          {className}
        </Text>
        .
      </>
    )
  );

  if (classId <= 0) {
    return (
      <Text fontSize="sm" color={theme.text.tertiary}>
        No makeup class is linked to this request.
      </Text>
    );
  }

  return (
    <Box>
      {!hideDescription ? (
        <Text fontSize="sm" color={theme.text.secondary} lineHeight="1.65" mb={4}>
          {intro}
        </Text>
      ) : null}

      <Flex align="center" justify="space-between" mb={3}>
        <IconButton
          aria-label="Previous month"
          icon={<HiOutlineChevronLeft />}
          size="sm"
          variant="ghost"
          onClick={() => shiftScheduleMonth(-1)}
          isDisabled={!canGoToPreviousMonth}
        />
        <Text fontSize="sm" fontWeight="600" color={theme.text.primary}>
          {formatMonthLabel(scheduleMonth, scheduleYear)}
        </Text>
        <IconButton
          aria-label="Next month"
          icon={<HiOutlineChevronRight />}
          size="sm"
          variant="ghost"
          onClick={() => shiftScheduleMonth(1)}
        />
      </Flex>

      {isLoadingSchedule ? (
        <Flex align="center" justify="center" py={8} gap={3}>
          <Spinner size="sm" color={theme.accent.primary} />
          <Text fontSize="sm" color={theme.text.secondary}>
            Loading schedule…
          </Text>
        </Flex>
      ) : futureSlots.length === 0 ? (
        <Text fontSize="sm" color={theme.text.tertiary} lineHeight="1.55">
          {laterWeek
            ? `No later ${className} sessions this month. Check the next month.`
            : "No upcoming sessions this month."}
        </Text>
      ) : (
        <Box>
          {futureSlots.map((entry, index) => {
            const key = scheduleSlotKey(entry);
            const isSelected =
              selectedSlot != null && scheduleSlotKey(selectedSlot) === key;
            const start = new Date(entry.start);
            const end = new Date(entry.end);
            const weekLabel = laterWeek
              ? makeupWeeksLaterLabel(referenceDate, start, studioClock.timeZone)
              : null;
            const timeLabel = formatScheduleSlotTimeRangeFromEntry(
              entry,
              studioClock.timeZone,
            );
            const dateLabel = formatScheduleSlotDateLabelFromEntry(
              entry,
              studioClock.timeZone,
            );
            const color = isSelected ? theme.accent.primary : theme.text.primary;
            const muted = isSelected ? theme.accent.primary : theme.text.secondary;

            return (
              <Flex
                key={key}
                as="button"
                type="button"
                aria-label={scheduleSlotAriaLabel({
                  className,
                  start,
                  end,
                  weekLabel,
                  timeZone: studioClock.timeZone,
                })}
                onClick={() => onSelectSlot(entry)}
                w="100%"
                direction={{ base: "column", md: "row" }}
                align={{ base: "flex-start", md: "center" }}
                justify="space-between"
                gap={{ base: 1, md: 4 }}
                py={3}
                px={0}
                borderRadius={0}
                borderBottomWidth={index === futureSlots.length - 1 ? 0 : "1px"}
                borderColor={theme.borderGlass.subtle}
                bg="transparent"
                textAlign="left"
                transition="color 0.15s ease"
                _hover={{
                  bg: isSelected ? "transparent" : theme.surfaceGlass.card,
                }}
              >
                <Box minW={0} flex="1">
                  <Text fontSize="sm" fontWeight="600" color={color} noOfLines={1}>
                    {className}
                  </Text>
                  <Text fontSize="sm" color={muted} lineHeight="1.5">
                    {dateLabel}
                  </Text>
                </Box>
                <Flex
                  direction="column"
                  align={{ base: "flex-start", md: "flex-end" }}
                  gap={0.5}
                  flexShrink={0}
                >
                  {weekLabel ? (
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color={isSelected ? theme.accent.primary : theme.text.tertiary}
                    >
                      {weekLabel}
                    </Text>
                  ) : null}
                  <Text fontSize="sm" color={muted}>
                    {timeLabel}
                  </Text>
                </Flex>
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
